import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";

import useListsObjects, { ObjectsListFilters } from "../../hooks/useListsObjects";
import useAuth from "../../hooks/useAuth";
import useImportEquipmentCsv from "../../hooks/useImportEquipmentCsv";
import useSites from "../../hooks/useSites";
import { useDebounce } from "../../hooks/useDebounce";
import { canImportEquipment } from "../../utils/roles";

import Alert from "../../components/alert/Alert";
import ManageAllConnectedObject from "../../components/manage-objects/ManageAllConnectedObject";
import EquipmentIcon from "../../components/icons/Equipment";
import SearchIcon from "../../components/icons/Search";
import UploadIcon from "../../components/icons/Upload";
import PageSectionHeader from "../../components/PageSectionHeader";

const selectClassName =
  "pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white";

const ALLOWED_ORDER_BY = [
  "id",
  "url",
  "createdAt",
  "updatedAt",
  "activatedAt",
  "isBlocked",
  "site",
  "designation",
  "location"
] as const;

type ObjectsOrderBy = (typeof ALLOWED_ORDER_BY)[number];
type ObjectsOrder = "ASC" | "DESC";

const DEFAULT_ORDER_BY: ObjectsOrderBy = "url";
const DEFAULT_ORDER: ObjectsOrder = "ASC";

type ListUrlPatch = {
  term?: string;
  siteId?: string;
  status?: "" | "active" | "blocked";
  assignment?: ObjectsListFilters["assignment"];
  page?: number;
  orderby?: ObjectsOrderBy;
  order?: ObjectsOrder;
};

const parsePage = (raw: string | null): number => {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
};

const parseOrderBy = (raw: string | null): ObjectsOrderBy => {
  if (raw && (ALLOWED_ORDER_BY as readonly string[]).includes(raw)) {
    return raw as ObjectsOrderBy;
  }
  return DEFAULT_ORDER_BY;
};

const parseOrder = (raw: string | null): ObjectsOrder =>
  raw?.toUpperCase() === "DESC" ? "DESC" : DEFAULT_ORDER;

const applyListParams = (current: URLSearchParams, patch: ListUrlPatch): URLSearchParams => {
  const next = new URLSearchParams(current);

  if ("term" in patch) {
    const term = patch.term?.trim() ?? "";
    if (term) next.set("term", term);
    else next.delete("term");
  }

  if ("siteId" in patch) {
    if (patch.siteId) next.set("siteId", patch.siteId);
    else next.delete("siteId");
  }

  if ("status" in patch) {
    if (patch.status === "active" || patch.status === "blocked") next.set("status", patch.status);
    else next.delete("status");
  }

  if ("assignment" in patch) {
    if (patch.assignment === "nonAffected") next.set("assignment", "nonAffected");
    else next.delete("assignment");
  }

  if ("orderby" in patch) {
    if (patch.orderby && patch.orderby !== DEFAULT_ORDER_BY) next.set("orderby", patch.orderby);
    else next.delete("orderby");
  }

  if ("order" in patch) {
    if (patch.order && patch.order !== DEFAULT_ORDER) next.set("order", patch.order);
    else next.delete("order");
  }

  if ("page" in patch) {
    const page = patch.page && patch.page > 1 ? patch.page : 1;
    if (page > 1) next.set("page", String(page));
    else next.delete("page");
  }

  return next;
};

const LitsObjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const intl = useIntl();
  const csvInput = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { error, isLoading, fetchListObjects, data, results } = useListsObjects();
  const {
    loading: isImportLoading,
    error: importError,
    response: importResponse,
    upload: uploadCsv
  } = useImportEquipmentCsv();
  const { sites, fetchSites } = useSites();
  const showImportCsv = canImportEquipment(user?.role);

  const termFromUrl = searchParams.get("term") ?? "";
  const siteId = searchParams.get("siteId") ?? "";
  const statusParam = searchParams.get("status");
  const status: "" | "active" | "blocked" =
    statusParam === "active" || statusParam === "blocked" ? statusParam : "";
  const assignment: ObjectsListFilters["assignment"] =
    searchParams.get("assignment") === "nonAffected" ? "nonAffected" : "affected";
  const showAffected = assignment !== "nonAffected";
  const page = parsePage(searchParams.get("page"));
  const orderby = parseOrderBy(searchParams.get("orderby"));
  const order = parseOrder(searchParams.get("order"));

  const [searchInput, setSearchInput] = useState(termFromUrl);
  const debouncedTerm = useDebounce(searchInput, 500) ?? "";

  const updateListParams = useCallback((
    patch: ListUrlPatch,
    options?: { replace?: boolean }
  ) => {
    setSearchParams(
      (prev) => applyListParams(prev, patch),
      { replace: options?.replace ?? false }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (debouncedTerm.trim() === termFromUrl.trim()) return;
    updateListParams({ term: debouncedTerm, page: 1 }, { replace: true });
  }, [debouncedTerm, termFromUrl, updateListParams]);

  const filters = useMemo<ObjectsListFilters>(() => ({
    term: termFromUrl,
    siteId: showAffected ? siteId : "",
    status: showAffected ? status : "",
    assignment,
    page,
    orderby,
    order
  }), [termFromUrl, siteId, status, assignment, page, showAffected, orderby, order]);

  const hasActiveFilters = Boolean(
    searchInput.trim() || siteId || status || !showAffected
  );

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    fetchListObjects(filters);
  }, [fetchListObjects, filters]);

  useEffect(() => {
    if (!importResponse) return;
    updateListParams({ page: 1 }, { replace: true });
  }, [importResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFetchPage = useCallback((nextPage: number) => {
    updateListParams({ page: nextPage });
  }, [updateListParams]);

  const onSortChange = useCallback((nextOrderBy: string, nextOrder: ObjectsOrder) => {
    updateListParams(
      {
        orderby: parseOrderBy(nextOrderBy),
        order: nextOrder,
        page: 1
      },
      { replace: true }
    );
  }, [updateListParams]);

  const onToggleAffected = (checked: boolean) => {
    if (checked) {
      updateListParams({ assignment: "affected", page: 1 }, { replace: true });
      return;
    }
    updateListParams(
      { assignment: "nonAffected", siteId: "", status: "", page: 1 },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchParams({}, { replace: true });
  };

  const onClickImport = () => {
    csvInput.current?.click();
  };

  const onSelectCsv = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadCsv(file);
    event.target.value = "";
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "equipment.list.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>

      <div className="h-full flex flex-col">
        <PageSectionHeader className="mb-8">
          <h2 className="text-2xl font-semibold" style={{ color: "#023865" }}>
            {intl.formatMessage(
              { id: "common.welcomeUser" },
              { firstName: user?.firstName, lastName: user?.lastName }
            )}
          </h2>
        </PageSectionHeader>

        <div className="flex flex-col gap-4 mb-6">
          <div className="relative w-full">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder={intl.formatMessage({ id: "equipment.list.searchPlaceholder" })}
              type="search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
            />
          </div>

          <label className="inline-flex items-center gap-3 cursor-pointer w-fit">
            <span className={`text-sm whitespace-nowrap ${showAffected ? "text-gray-400" : "font-semibold text-[#023865]"}`}>
              {intl.formatMessage({ id: "equipment.list.showNonAffectedIds" })}
            </span>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={showAffected}
              onChange={(e) => onToggleAffected(e.target.checked)}
              aria-label={intl.formatMessage({
                id: showAffected
                  ? "equipment.list.showAffectedIds"
                  : "equipment.list.showNonAffectedIds"
              })}
            />
            <div
              className="relative w-14 h-8 bg-gray-300 rounded-full
              peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-200
              peer-checked:bg-[#E94E1B]
              after:content-[''] after:absolute after:top-1 after:start-1 after:bg-white
              after:rounded-full after:h-6 after:w-6 after:transition-all
              peer-checked:after:translate-x-6"
            />
            <span className={`text-sm whitespace-nowrap ${showAffected ? "font-semibold text-[#023865]" : "text-gray-400"}`}>
              {intl.formatMessage({ id: "equipment.list.showAffectedIds" })}
            </span>
          </label>

          <div className="flex flex-col lg:flex-row lg:justify-between gap-3">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
              {showAffected && (
                <>
                  <select
                    className={selectClassName}
                    value={siteId}
                    onChange={(e) => {
                      updateListParams(
                        { siteId: e.target.value, page: 1 },
                        { replace: true }
                      );
                    }}
                    aria-label={intl.formatMessage({ id: "equipment.list.filterBySite" })}
                  >
                    <option value="">{intl.formatMessage({ id: "equipment.list.allSites" })}</option>
                    {sites?.map((site) => (
                      <option key={site.id} value={String(site.id)}>
                        {site.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className={selectClassName}
                    value={status}
                    onChange={(e) => {
                      updateListParams(
                        {
                          status: e.target.value as "" | "active" | "blocked",
                          page: 1
                        },
                        { replace: true }
                      );
                    }}
                    aria-label={intl.formatMessage({ id: "equipment.list.filterByStatus" })}
                  >
                    <option value="">{intl.formatMessage({ id: "equipment.list.allStatuses" })}</option>
                    <option value="active">{intl.formatMessage({ id: "common.active" })}</option>
                    <option value="blocked">{intl.formatMessage({ id: "common.locked" })}</option>
                  </select>
                </>
              )}

              {hasActiveFilters && (
                <button
                  type="button"
                  className="px-4 py-3 text-sm text-orange-600 hover:text-orange-700 whitespace-nowrap"
                  onClick={resetFilters}
                >
                  {intl.formatMessage({ id: "equipment.list.resetFilters" })}
                </button>
              )}
            </div>

            <div className="flex gap-3 shrink-0">
              <button
                className="px-4 py-3 flex items-center gap-2 text-white rounded-lg"
                type="button"
                style={{ backgroundColor: "#E94E1B" }}
                onClick={() => {
                  const listSearch = searchParams.toString();
                  navigate("add", {
                    state: { listSearch: listSearch ? `?${listSearch}` : "" }
                  });
                }}
              >
                <span className="text-xl">+</span>
                {intl.formatMessage({ id: "equipment.list.addObject" })}
              </button>
              {showImportCsv && (
                <>
                  <button
                    className="px-4 py-3 flex items-center gap-2 text-white rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
                    type="button"
                    style={{ backgroundColor: "#E94E1B" }}
                    onClick={onClickImport}
                    disabled={isImportLoading}
                  >
                    <UploadIcon className="w-5 h-5" />
                    {isImportLoading
                      ? intl.formatMessage({ id: "equipment.list.importing" })
                      : intl.formatMessage({ id: "equipment.list.importCsv" })}
                  </button>
                  <input
                    ref={csvInput}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={onSelectCsv}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {error && <Alert className="my-5 w-fit" type="error" message={error.message} />}
        {importError && <Alert className="my-5 w-fit" type="error" message={importError.message} />}
        {importResponse && (
          <Alert
            className="my-5 w-fit"
            type="success"
            message={intl.formatMessage(
              { id: "equipment.list.importSummary" },
              {
                message: importResponse.message,
                imported: importResponse.imported,
                updated: importResponse.updated,
                skipped: importResponse.skipped
              }
            )}
          />
        )}

        <section className="bg-white rounded-2xl p-6" style={{ border: "1px solid #E4E7EC" }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <EquipmentIcon className="w-6 h-6 text-gray-700 mb-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.list.sectionTitle" })}
              </h3>
            </div>
            {results && (
              <p className="text-sm text-gray-600">
                {intl.formatMessage(
                  { id: "equipment.list.objectCount" },
                  { count: results.totalItems }
                )}
              </p>
            )}
          </div>

          {results ? (
            <ManageAllConnectedObject
              allObj={results}
              data={data}
              onFetchPage={onFetchPage}
              orderby={orderby}
              order={order}
              onSortChange={onSortChange}
            />
          ) : !isLoading ? (
            <p className="text-center text-gray-500 py-8">
              {intl.formatMessage({ id: "equipment.list.empty" })}
            </p>
          ) : null}

          {isLoading && (
            <p className="my-5 text-center text-xs">
              {intl.formatMessage({ id: "common.loading" })}
            </p>
          )}
        </section>
      </div>
    </>
  );
};

export default LitsObjects;
